/**
 * Complementary & Supplementary Angles generator (6.MGSR.2.2).
 *
 * Given one angle in a complementary or supplementary pair, find the other; or
 * classify the relationship between two angles. The given angle is chosen so
 * the missing angle is a positive whole number, and the missing value is
 * computed with the geometry helpers, so display and answer always agree.
 *
 * Families:
 *   F1: complementary  -> find the other (sum 90)
 *   F2: supplementary  -> find the other (sum 180)
 *   F3: classify the relationship (choice)
 *   F4: solve for the missing angle in a described pair (number)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { complementaryAngle, supplementaryAngle } from "../engine/geometry";
import type { AngleDiagramModel } from "../graph/plot-model";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "angle-relationships" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly step: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], step: 5 },
  standard: { families: ["F1", "F2", "F3"], step: 5 },
  hard: { families: ["F1", "F2", "F3", "F4"], step: 1 },
  challenge: { families: ["F2", "F3", "F4"], step: 1 },
};

export class AngleRelationshipsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      if (family === "F1" || family === "F4") {
        // Complementary pair: pick given so the complement is a positive whole.
        const given = rng.int(1, Math.floor(89 / cfg.step)) * cfg.step;
        const other = complementaryAngle(rat(given));
        if (other === null) continue;
        const graph: AngleDiagramModel = {
          kind: "angle-diagram",
          rays: [0, given],
          label: `${given}\u00b0`,
        };
        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: {
            text: `Two angles are complementary. One measures ${given}\u00b0. What is the measure of the other angle, in degrees?`,
          },
          answerType: "number",
          answer: { kind: "number", value: other },
          steps: [
            {
              description: "Complementary angles sum to 90\u00b0, so subtract the known angle from 90\u00b0.",
              latex: `90^{\\circ} - ${given}^{\\circ} = ${other.toLatex()}^{\\circ}`,
            },
          ],
          hints: [
            { level: 1, text: "Complementary angles add up to 90\u00b0." },
            { level: 2, text: `Subtract ${given} from 90.` },
          ],
          subskills: topic.subskills,
          graph,
        };
      }

      if (family === "F2") {
        const given = rng.int(1, Math.floor(179 / cfg.step)) * cfg.step;
        const other = supplementaryAngle(rat(given));
        if (other === null) continue;
        const graph: AngleDiagramModel = {
          kind: "angle-diagram",
          rays: [0, given],
          label: `${given}\u00b0`,
        };
        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: {
            text: `Two angles are supplementary. One measures ${given}\u00b0. What is the measure of the other angle, in degrees?`,
          },
          answerType: "number",
          answer: { kind: "number", value: other },
          steps: [
            {
              description: "Supplementary angles sum to 180\u00b0, so subtract the known angle from 180\u00b0.",
              latex: `180^{\\circ} - ${given}^{\\circ} = ${other.toLatex()}^{\\circ}`,
            },
          ],
          hints: [
            { level: 1, text: "Supplementary angles add up to 180\u00b0." },
            { level: 2, text: `Subtract ${given} from 180.` },
          ],
          subskills: topic.subskills,
          graph,
        };
      }

      // F3: classify the relationship between two angles (choice).
      const isComp = rng.bool();
      const a = isComp
        ? rng.int(1, Math.floor(89 / cfg.step)) * cfg.step
        : rng.int(1, Math.floor(179 / cfg.step)) * cfg.step;
      const sum = isComp ? 90 : 180;
      const b = sum - a;
      if (b <= 0) continue;
      const correctId = isComp ? "complementary" : "supplementary";
      const choices: Choice[] = [
        { id: "complementary", label: "Complementary (sum 90\u00b0)" },
        { id: "supplementary", label: "Supplementary (sum 180\u00b0)" },
        { id: "neither", label: "Neither" },
      ];
      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Two angles measure ${a}\u00b0 and ${b}\u00b0. What is their relationship?`,
        },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [
          {
            description: "Add the two angles. A sum of 90\u00b0 is complementary; a sum of 180\u00b0 is supplementary.",
            latex: `${a}^{\\circ} + ${b}^{\\circ} = ${sum}^{\\circ}`,
          },
        ],
        hints: [
          { level: 1, text: "Add the two measures together." },
          { level: 2, text: "90\u00b0 means complementary; 180\u00b0 means supplementary." },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "choice"
        ? q.answer.value
        : q.answer.kind === "number"
          ? q.answer.value.key()
          : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const angleRelationships = new AngleRelationshipsGenerator();
